import api, { type RequestOptions } from './api';
import { MAX_PAGE_SIZE } from '../utils/constants';

// Tenant types based on API response
export interface Tenant {
  id: string;
  name: string;
  domain: string;
  description?: string;
  // Free-text "Organization Name" (ten_org_name). Display only — this is NOT
  // the channel, and must never be read for scoping or authorization.
  organizationName?: string;
  // Owning channel; null for legacy "direct" tenants created before the layer.
  channelId?: string | null;
  createTime?: string;
  updateTime?: string;
}

export interface TenantListParams {
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface TenantCreateData {
  name: string;
  domain: string;
  description?: string;
  organizationName?: string;
  defaultRole?: string;
  // Optional owning channel. A SuperAdmin may name any channel. A ChannelAdmin
  // may omit it when they administer exactly one channel; with several, the
  // server returns 400 asking which. Naming a channel they don't administer is 403.
  channelId?: string;
}

export interface TenantUpdateData {
  name?: string;
  domain?: string;
  description?: string;
  organizationName?: string;
}

export interface PaginatedTenantResponse {
  items: Tenant[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface SharedGateway {
  mtgeId: string;
  name: string;
  serialNumber: string;
  status: string;
  lastSeen: string | null;
  wanIp: string | null;
  tenantSlot: number;
  vrfId: number;
  onboardStatus: string;
  onboardedAt: string | null;
  internetBreakout: boolean;
  tunnelProtocol: string;
}

// `options` exists so the header's scope picker can opt out of the global
// loading overlay — it fetches on every page load.
const listTenants = async (params?: TenantListParams, options?: RequestOptions) => {
  const response = await api.get('/tenants', { params, ...options } as RequestOptions);
  return response.data as PaginatedTenantResponse;
};

const tenantService = {
  list: listTenants,

  // Every tenant the caller can see, paged out in full.
  //
  // The API caps pageSize at MAX_PAGE_SIZE and 400s above it rather than
  // clamping, so callers that need a complete set (e.g. building a
  // tenant -> channel map) have to walk the pages instead of over-asking.
  listAll: async (params?: Omit<TenantListParams, 'page' | 'pageSize'>, options?: RequestOptions) => {
    const items: Tenant[] = [];
    let page = 1;
    let totalPages = 1;

    do {
      const response = await listTenants({ ...params, page, pageSize: MAX_PAGE_SIZE }, options);
      items.push(...response.items);
      totalPages = response.pagination?.totalPages ?? 1;
      page += 1;
    } while (page <= totalPages);

    return items;
  },

  getById: async (id: string) => {
    const response = await api.get(`/tenants/${id}`);
    return response.data as Tenant;
  },

  create: async (data: TenantCreateData) => {
    const response = await api.post('/tenants', data);
    return response.data as Tenant;
  },

  update: async (id: string, data: TenantUpdateData) => {
    const response = await api.put(`/tenants/${id}`, data);
    return response.data as Tenant;
  },

  delete: async (id: string) => {
    await api.delete(`/tenants/${id}`);
  },

  // Get list of tenants for dropdown selection
  getSelect: async () => {
    const response = await api.get('/tenants/select');
    return response.data as Tenant[];
  },

  // Get list of GVSA-registered tenants for edge deployment
  getGvsaRegisteredSelect: async () => {
    const response = await api.get('/tenants/select/gvsa-registered');
    return response.data as Tenant[];
  },

  // Register tenant with Global VSA
  registerWithGlobalVsa: async (id: string, data: { orchestratorUrl: string }) => {
    const response = await api.post(`/tenants/${id}/register-global-vsa`, data);
    return response.data;
  },

  getGlobalVsaStatus: async (id: string) => {
    const response = await api.get(`/tenants/${id}/global-vsa-status`);
    return response.data;
  },

  getSharedGateways: async (tenantId: string) => {
    const response = await api.get(`/tenants/${tenantId}/shared-gateways`);
    return response.data as SharedGateway[];
  },

};

export default tenantService;

